import { z } from 'zod';
import { NotificationCapabilityIdSchema, NotificationCapabilityMaskSchema, } from './capabilities';
export const AppNotificationResourceTypeSchema = z.enum([
    'ORDER',
    'RECURRING_ORDER',
    'PRODUCT',
    'DISTRIBUTOR',
    'PAYOUT',
    'ACCOUNT',
]);
// ============================================================================
// App Notification Metadata
// ============================================================================
/**
 * Metadata attached to notifications for context and deep-linking.
 * Each notification type may use different metadata fields.
 */
export const AppNotificationMetadataSchema = z.object({
    // Order-related metadata
    orderId: z.string().uuid().optional(),
    poNumber: z.string().optional(),
    // Recurring order metadata
    recurringOrderId: z.string().uuid().optional(),
    recurringOrderName: z.string().optional(),
    // Distributor metadata
    distributorId: z.string().uuid().optional(),
    distributorName: z.string().optional(),
    // Product metadata
    productId: z.string().uuid().optional(),
    productName: z.string().optional(),
    // Payout metadata
    payoutId: z.string().uuid().optional(),
    // UI navigation hints
    settingsTab: z.string().optional(),
});
// ============================================================================
// Notification Preferences - Marketplace Section
// ============================================================================
export const NotificationPreferencesMarketplaceSchema = z.object({
    /** Order status updates (placed, accepted, shipped, delivered) */
    orderUpdates: z.boolean().default(true),
    /** Order cancellation notifications */
    orderCancellations: z.boolean().default(true),
    /** Recurring order notifications (created, executed, paused, resumed, deleted) */
    recurringOrders: z.boolean().default(true),
    /** Product and distributor alerts (deactivation notices) */
    productAlerts: z.boolean().default(true),
});
// ============================================================================
// Notification Preferences - Payouts Section
// ============================================================================
export const NotificationPreferencesPayoutsSchema = z.object({
    /** Payout lifecycle updates (initiated, completed, failed) */
    payoutUpdates: z.boolean().default(true),
});
// ============================================================================
// Notification Preferences - System Section
// ============================================================================
export const NotificationPreferencesSystemSchema = z.object({
    /** System alert notifications */
    systemAlerts: z.boolean().default(true),
    /** System announcement notifications */
    announcements: z.boolean().default(true),
    /** Account activity notifications */
    accountActivity: z.boolean().default(true),
});
// ============================================================================
// Notification Preferences - Reports Section
// ============================================================================
export const NotificationPreferencesReportsSchema = z.object({
    /** Nightly business activity reports */
    nightly: z.boolean().default(false),
    /** Monthly account statements */
    monthlyStatements: z.boolean().default(false),
    /** Per-business recurring billing summaries */
    billingSummaries: z.boolean().default(false),
});
// ============================================================================
// Notification Preferences (Business-level)
// ============================================================================
/**
 * Business-level notification preferences.
 * Controls which notification types are enabled for the entire business.
 * All preferences default to true (enabled).
 */
export const NotificationPreferencesSchema = z.object({
    marketplace: NotificationPreferencesMarketplaceSchema.optional().default({}),
    payouts: NotificationPreferencesPayoutsSchema.optional().default({}),
    reports: NotificationPreferencesReportsSchema.optional().default({}),
    system: NotificationPreferencesSystemSchema.optional().default({}),
});
// ============================================================================
// User Notification Preferences
// ============================================================================
/**
 * User-level notification preferences.
 * Allows users to override business preferences or disable notifications entirely.
 */
export const UserNotificationPreferenceOverridesSchema = z.object({
    marketplace: NotificationPreferencesMarketplaceSchema.partial().optional(),
    payouts: NotificationPreferencesPayoutsSchema.partial().optional(),
    reports: NotificationPreferencesReportsSchema.partial().optional(),
    system: NotificationPreferencesSystemSchema.partial().optional(),
});
export const UserNotificationChannelsSchema = z.object({
    mobile: z.boolean().default(true),
    email: z.boolean().default(true),
    sms: z.boolean().default(true),
});
export const RequiredUserNotificationsSchema = z.object({
    securityLoginAuthCodes: z.literal(true).default(true),
});
export const UserNotificationPreferencesSchema = z.object({
    /** Master switch to enable/disable all notifications for this user */
    enabled: z.boolean().default(true),
    /** If true, inherit preferences from business; if false, use overrides */
    inheritBusinessPreferences: z.boolean().default(true),
    /** Delivery channels available for optional notifications */
    channels: UserNotificationChannelsSchema.optional().default({}),
    /** Sparse user-specific overrides to the business notification preferences */
    overrides: UserNotificationPreferenceOverridesSchema.optional(),
    /** Security notifications that cannot be disabled by user preferences */
    requiredNotifications: RequiredUserNotificationsSchema.optional().default({}),
});
/**
 * Caller-safe view used by profile, user-management, device-sync, and admin
 * preference endpoints. Ordinary callers receive only visible capability IDs;
 * platform-admin responses may include the complete mask.
 */
export const UserNotificationPreferencesViewSchema = z.object({
    notificationPreferences: UserNotificationPreferencesSchema,
    effectivePreferences: UserNotificationPreferenceOverridesSchema,
    capabilities: NotificationCapabilityMaskSchema,
    staleOverridePaths: z.array(NotificationCapabilityIdSchema).optional(),
});
export const BusinessNotificationPreferencesViewSchema = z.object({
    preferences: UserNotificationPreferenceOverridesSchema,
    capabilities: NotificationCapabilityMaskSchema,
});
export const DEFAULT_USER_NOTIFICATION_PREFERENCES = UserNotificationPreferencesSchema.parse({});
// ============================================================================
// Default Notification Preferences
// ============================================================================
/**
 * Default notification preferences with all notifications enabled.
 * Used when no preferences are set.
 */
export const DEFAULT_NOTIFICATION_PREFERENCES = {
    marketplace: {
        orderUpdates: true,
        orderCancellations: true,
        recurringOrders: true,
        productAlerts: true,
    },
    payouts: {
        payoutUpdates: true,
    },
    reports: {
        nightly: false,
        monthlyStatements: false,
        billingSummaries: false,
    },
    system: {
        systemAlerts: true,
        announcements: true,
        accountActivity: true,
    },
};
